import BigNumber from 'bignumber.js'
import { nextTick } from 'vue'

BigNumber.config({
  FORMAT: {
    // string to prepend
    prefix: '',
    // decimal separator
    decimalSeparator: '.',
    // grouping separator of the integer part
    groupSeparator: ',',
    // primary grouping size of the integer part
    groupSize: 3,
    // secondary grouping size of the integer part
    secondaryGroupSize: 0,
    // grouping separator of the fraction part
    fractionGroupSeparator: ' ',
    // grouping size of the fraction part
    fractionGroupSize: 0,
    // string to append
    suffix: '',
  },
})

/**
 * 数字格式化
 * @param num 值
 * @param decimal 精度
 * @returns 1,000
 */
export const toFormat = (num, decimal = -1) => {
  if (!num) return 0
  if (decimal === -1) {
    decimal = BigNumber(num).dp()
  }
  return BigNumber(num).toFormat(+decimal, 1)
}

/**
 *  保留小数
 * @param num 值
 * @param decimal 精度
 * @returns 1.00
 */
export const toFixed = (num, decimal = -1) => {
  if (!num) return 0
  if (decimal === -1) {
    decimal = BigNumber(num).dp()
  }
  return BigNumber(num).toFixed(+decimal, 1)
}

/**
 * 算术
 * 文档 https://mikemcl.github.io/bignumber.js/#bignumber
 * plus 加
 * minus 减
 * div 除
 * times 乘
 *
 * example BNumber(100).plus(10).div(20) // 5.5
 */
export const BNumber = BigNumber

/**
 * 限制输入为数字
 * @param value 值
 * @param decimal 精度
 * @returns string
 */
export const inputLimitToDigit = (value, decimal = 2) => {
  const reg = decimal === 0 ? /^\d+$/ : new RegExp(`^\\d+((\\.?)|(\\.?\\d{0,${decimal}}))$`)
  // true
  if (reg.test(value)) {
    return value
  }
  // NaN
  if (isNaN(Number(value))) {
    return ''
  }
  //
  if (/\./.test(value)) {
    const [val, dec] = value.split('.')

    if (decimal === 0) {
      return val
    }

    return value.substring(-1, value.length - (dec.length - decimal))
  }

  return ''
}

export function toFormatUnit(num, decimal = -1) {
  if (Math.abs(num) >= 1.0e12) {
    return toFormat(num / 1.0e12) + 'T'
  } else if (Math.abs(num) >= 1.0e9) {
    return toFormat(num / 1.0e9) + 'B'
  } else if (Math.abs(num) >= 1.0e6) {
    return toFormat(num / 1.0e6) + 'M'
  } else if (Math.abs(num) >= 1.0e3) {
    return toFormat(num / 1.0e3) + 'K'
  }
  return toFormat(num, decimal)
}

/**
 * 去掉千分位逗号，拿到原始数字字符串
 * @param value 任意输入
 * @returns string
 */
export const stripThousands = (value) => String(value ?? '').replace(/,/g, '')

/**
 * 数字字符串千分位格式化，保留小数原样
 * @param value 数字或字符串（允许带逗号）
 * @param decimal 限制小数位，-1 表示不限制
 * @returns 1,234.56
 */
export const formatThousands = (value, decimal = -1) => {
  const raw = stripThousands(value)
  if (raw === '' || raw === '-') return raw
  const limited = decimal >= 0 ? inputLimitToDigit(raw, decimal) : raw
  if (limited === '') return ''
  const [intPart, decPart] = String(limited).split('.')
  const intNum = intPart ? Number(intPart) : 0
  if (!Number.isFinite(intNum)) return ''
  const formattedInt = intNum.toLocaleString('en-US')
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt
}

/**
 * 生成 uni-app input 的千分位 @input 处理函数
 *   <input type="text" v-model="amount" @input="onAmountInput" />
 *   const onAmountInput = createThousandsInput(amount, { decimal: 2, max: () => stockInfo.value.maxQty })
 *
 * @param valueRef v-model 绑定的 ref
 * @param options.decimal 小数位限制，默认 -1 不限制；0 表示整数
 * @param options.max 上限，可为数字或函数
 * @returns (e) => void
 */
export const createThousandsInput = (valueRef, options = {}) => {
  const { decimal = -1, max } = options
  return (e) => {
    const rawInput = e?.detail?.value ?? ''
    const decimalVal = typeof decimal === 'function' ? decimal() : decimal
    // 先滤掉非数字字符并合并多余小数点，避免 inputLimitToDigit 对混合输入（如 "123a"）返回空串把已输内容整体清掉
    let value = stripThousands(rawInput).replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
    // decimal < 0 不限制小数位；inputLimitToDigit 对负 decimal 会构造非法正则。
    if (decimalVal >= 0) {
      value = inputLimitToDigit(value, decimalVal)
    }
    const maxVal = typeof max === 'function' ? max() : max
    const maxNum = Number(maxVal)
    // 仅当 max 是正数时才夹取；0/NaN/undefined 视为"无上限"，避免后端返回 0 时用户怎么输入都被清回 0。
    if (Number.isFinite(maxNum) && maxNum > 0 && Number(value) > maxNum) {
      value = String(maxNum)
    }
    const formatted = formatThousands(value, decimalVal)
    // 当格式化结果与上一次相同、但 DOM 仍残留用户输入的非法字符时（如空框输入 "a"、"1" 后追加 "a"），
    // 仅赋值同一个字符串 Vue diff 不会刷新输入框；先把 ref 对齐成 DOM 实际内容（必定不同于 formatted，
    // 能触发一次渲染），再 nextTick 恢复成过滤后的值。原先"先清空再恢复"的写法在 ref 本身就是
    // 空串时（空框输字母）两次赋值都无变化，DOM 不会刷新，字母会留在输入框里。
    if (valueRef.value === formatted && rawInput !== formatted) {
      valueRef.value = rawInput
      nextTick(() => {
        valueRef.value = formatted
      })
    } else {
      valueRef.value = formatted
    }
  }
}

// 展示%数字
export const toFormatPercent = (num, decimal = 2) => {
  return `${toFormat(Number(num).toFixed(decimal), decimal)}%`
}
